import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Percent, AlertTriangle } from "lucide-react";

import { referralApi } from "@/lib/extensions-api";
import { formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { toast } from "sonner";

export function ReferralConfigPage() {
  const qc = useQueryClient();
  const [newRate, setNewRate] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const current = useQuery({
    queryKey: ["admin-referral-config"],
    queryFn: referralApi.admin.getConfig,
  });
  const history = useQuery({
    queryKey: ["admin-referral-config-history"],
    queryFn: referralApi.admin.configHistory,
  });

  const publish = useMutation({
    mutationFn: (rate: string) => referralApi.admin.publishConfig(rate),
    onSuccess: () => {
      toast.success("New commission rate published.");
      qc.invalidateQueries({ queryKey: ["admin-referral-config"] });
      qc.invalidateQueries({ queryKey: ["admin-referral-config-history"] });
      setNewRate("");
      setConfirmOpen(false);
    },
    onError: () => setConfirmOpen(false),
  });

  const currentPct = current.data
    ? (Number(current.data.commission_rate) * 100).toFixed(2)
    : "—";
  const newPct = newRate ? (Number(newRate) * 100).toFixed(2) : "—";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Referral Commission Configuration</h1>
        <p className="text-sm text-surface-500 mt-1">
          Configure the commission rate applied to every qualifying wallet credit.
          Historical transactions retain the version they were processed under.
        </p>
      </header>

      <Card>
        <CardHeader><CardTitle>Current Rate</CardTitle></CardHeader>
        <CardBody>
          {current.isLoading ? <Skeleton className="h-20" /> : current.data ? (
            <div className="flex items-baseline gap-4">
              <div className="text-6xl font-bold text-brand-700">{currentPct}%</div>
              <div className="text-sm text-surface-500">
                <div>Version <b>#{current.data.version_number}</b></div>
                <div>Effective from {formatDateTime(current.data.effective_from)}</div>
                <div>Published by {current.data.created_by}</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-warning-700 bg-warning-50 p-3 rounded-lg border border-warning-200">
              No active rate configured. Publish one below.
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Publish New Version</CardTitle></CardHeader>
        <CardBody className="space-y-4">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 flex gap-2 text-xs text-warning-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            Enter as a decimal — for example <code className="font-mono">0.05</code> for 5%.
            The current version will be marked EXPIRED and this becomes effective immediately.
          </div>
          <Input
            label="New Commission Rate"
            type="number"
            step="0.001"
            min="0"
            max="1"
            placeholder="0.05"
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            leftIcon={<Percent className="h-4 w-4" />}
            hint={newRate ? `Displayed as ${newPct}%` : undefined}
          />
          <Button
            disabled={!newRate || Number(newRate) < 0 || Number(newRate) > 1}
            onClick={() => setConfirmOpen(true)}
          >
            Publish
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Version History</CardTitle></CardHeader>
        <CardBody className="p-0">
          {history.isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Version</TH>
                  <TH className="text-right">Rate</TH>
                  <TH>Status</TH>
                  <TH>Effective From</TH>
                  <TH>Effective Until</TH>
                  <TH>Published By</TH>
                </TR>
              </THead>
              <tbody>
                {(history.data ?? []).map((v) => (
                  <TR key={v.id}>
                    <TD className="font-mono">#{v.version_number}</TD>
                    <TD className="text-right font-semibold">
                      {(Number(v.commission_rate) * 100).toFixed(2)}%
                    </TD>
                    <TD><StatusPill status={v.status} /></TD>
                    <TD className="text-xs text-surface-500">{formatDateTime(v.effective_from)}</TD>
                    <TD className="text-xs text-surface-500">
                      {v.effective_until ? formatDateTime(v.effective_until) : "—"}
                    </TD>
                    <TD className="text-xs text-surface-600">{v.created_by}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Publish New Rate?"
        message={`Publishing ${newPct}% will replace the current rate. Historical transactions remain unchanged.`}
        confirmLabel="Publish"
        loading={publish.isPending}
        onConfirm={() => publish.mutate(newRate)}
      />
    </div>
  );
}
